// Reunderwriting Module (Tier 3) PR 4 — detail drawer with mark-reviewed /
// resolve flow. Reuses the shared <Modal> primitive.

import { useEffect, useState } from "react";
import { AlertOctagon } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button, FormField, Textarea } from "@/design-system/components/ui";
import type { FailureStatus, ReunderwritingFailureDto } from "@/types/reunderwriting";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  open: boolean;
  failure: ReunderwritingFailureDto | null;
  canResolve: boolean;
  isLoading: boolean;
  onClose: () => void;
  onMarkReviewed: (notes: string) => Promise<void>;
  onResolve: (resolutionNotes: string) => Promise<void>;
}

export function ReunderwritingFailureDetailDrawer({
  open,
  failure,
  canResolve,
  isLoading,
  onClose,
  onMarkReviewed,
  onResolve,
}: Props) {
  const [nextStatus, setNextStatus] = useState<FailureStatus>("Reviewed");
  const [notes, setNotes] = useState("");
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { dateTime } = useDateFormatter();

  useEffect(() => {
    if (failure) {
      setNextStatus(failure.status === "Open" ? "Reviewed" : failure.status);
      setNotes(failure.resolutionNotes ?? "");
      setCertified(failure.certifiedResolved);
      setError(null);
    }
  }, [failure]);

  const handleSubmit = async () => {
    if (!failure) return;
    setError(null);
    if (nextStatus === "Resolved" && !certified) {
      setError("Tick the certification checkbox to mark a failure Resolved.");
      return;
    }
    if (nextStatus === "Resolved" && !notes.trim()) {
      setError("Resolution notes are required to mark a failure Resolved.");
      return;
    }
    setSubmitting(true);
    try {
      if (nextStatus === "Resolved") {
        await onResolve(notes);
      } else {
        await onMarkReviewed(notes);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Failure detail"
      description={failure ? `Failure ${failure.id.slice(0, 8)} — Lane ${failure.lane}` : undefined}
      size="lg"
      footer={
        canResolve && failure ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Saving…" : "Save"}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {isLoading || !failure ? (
        <div role="status" aria-live="polite">Loading…</div>
      ) : (
        <div className="flex flex-col gap-space-4">
          <dl className="grid grid-cols-2 gap-space-3 text-body">
            <div>
              <dt className="text-label uppercase tracking-label text-gray-mid">Status</dt>
              <dd className="text-onyx font-semibold">{failure.status}</dd>
            </div>
            <div>
              <dt className="text-label uppercase tracking-label text-gray-mid">Lane</dt>
              <dd className="text-onyx font-mono">{failure.lane}</dd>
            </div>
            <div>
              <dt className="text-label uppercase tracking-label text-gray-mid">Created</dt>
              <dd className="text-onyx">{dateTime(failure.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-label uppercase tracking-label text-gray-mid">Analyst</dt>
              <dd className="text-onyx">{failure.analystUserId}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-label uppercase tracking-label text-gray-mid">Source</dt>
              <dd className="text-onyx">
                {failure.checklistItemId
                  ? `Item-level (checklist item ${failure.checklistItemId.slice(0, 8)})`
                  : "Lane-level fail"}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-label uppercase tracking-label text-gray-mid">Linked case</dt>
              <dd>
                <a
                  className="text-gold underline"
                  href={`/reunderwriting/cases/${failure.reunderwritingCaseId}`}
                >
                  Open case →
                </a>
              </dd>
            </div>
          </dl>

          <FormField htmlFor="ru-failure-reason" label="Failure reason">
            <Textarea
              id="ru-failure-reason"
              value={failure.failureReason}
              readOnly
              rows={3}
              aria-readonly="true"
            />
          </FormField>

          {!canResolve && (
            <div
              role="alert"
              className="flex items-center gap-space-2 text-small text-status-warning bg-white-warm border border-status-warning/40 rounded p-space-2"
            >
              <AlertOctagon size={16} aria-hidden />
              <span>You don&apos;t have permission to resolve failures.</span>
            </div>
          )}

          {canResolve && (
            <fieldset className="flex flex-col gap-space-3">
              <legend className="font-display text-label uppercase tracking-label text-onyx">
                Executive review
              </legend>

              <div role="radiogroup" aria-label="New status" className="flex flex-col gap-space-1">
                {(["Reviewed", "Resolved"] as const).map((s) => (
                  <label key={s} className="inline-flex items-center gap-space-2 text-body">
                    <input
                      type="radio"
                      name="ru-failure-status"
                      value={s}
                      checked={nextStatus === s}
                      onChange={() => setNextStatus(s)}
                    />
                    <span>Mark {s}</span>
                  </label>
                ))}
              </div>

              <FormField
                htmlFor="ru-failure-notes"
                label="Resolution notes"
                required={nextStatus === "Resolved"}
              >
                <Textarea
                  id="ru-failure-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="What did the executive review confirm?"
                />
              </FormField>

              {nextStatus === "Resolved" && (
                <label className="inline-flex items-center gap-space-2 text-body">
                  <input
                    type="checkbox"
                    checked={certified}
                    onChange={(e) => setCertified(e.target.checked)}
                    aria-required="true"
                  />
                  <span>I have reviewed and resolved this failure.</span>
                </label>
              )}

              {error && (
                <p role="alert" className="text-small text-status-error">
                  {error}
                </p>
              )}
            </fieldset>
          )}
        </div>
      )}
    </Modal>
  );
}
